import { Component, DestroyRef, OnInit } from '@angular/core';
import { API, FixedAspectRatioImageDTO, ProductCategoryFullDTO, SingleFileUpload } from '../../../services/api/api';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '../../../services/error.service';
import { ModalService } from '../../../services/gui/modal/modal.service';
import { UtilService } from '../../../services/util.service';
import { Clipping } from '../../../components/image/model';
import { ImageUploadModel } from '../../../services/gui/image/model';
import { ImageUploadService } from '../../../services/gui/image/image-upload.service';
import { TranslateService } from '@ngx-translate/core';
import { NavigationService } from '@ngx-seatback/utils';
import { SbWizardComponent, SbWizardTabComponent } from '@ngx-seatback/layout';
import { finalize, take } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'sb-page-product-categories-edit',
  templateUrl: './product-categories-edit.component.html',
  styleUrls: ['./product-categories-edit.component.scss'],
  standalone: false,
})
export class ProductCategoriesEditComponent implements OnInit {
  productCategory: ProductCategoryFullDTO;
  productCategoryId: number;
  loading: boolean;
  form: UntypedFormGroup;
  AmbianceImageUpload: ImageUploadModel;
  ambianceUrl: string = './assets/img/channel-placeholder.png';

  constructor(
    private activatedRoute: ActivatedRoute,
    private api: API,
    public errorHandler: ErrorService,
    public modal: ModalService,
    public util: UtilService,
    private imageUploadService: ImageUploadService,
    private translate: TranslateService,
    private navService: NavigationService,
    private destroyRef: DestroyRef
  ) {
    this.AmbianceImageUpload = new ImageUploadModel();

    this.productCategoryId = null;
    this.loading = true;
    this.form = new UntypedFormGroup({
      Name: new UntypedFormControl('', [Validators.required]),
      Description: new UntypedFormControl('', []),
    });
  }

  get Name() {
    return this.form.get('Name');
  }

  get Description() {
    return this.form.get('Description');
  }

  ngOnInit(): void {
    this.activatedRoute.params.subscribe(params => {
      this.productCategoryId = params.productCategoryID;
      this.refreshFromServer();
    });
    this.registerFormChangesListener();
  }

  private registerFormChangesListener() {
    this.Name.valueChanges.subscribe(value => {
      this.productCategory.name = value;
    });
    this.Description.valueChanges.subscribe(value => {
      this.productCategory.description = value;
    });
  }

  updateForm() {
    this.Name.setValue(this.productCategory.name);
    this.Name.markAsTouched();
    this.Description.setValue(this.productCategory.description);
    this.Description.markAsTouched();
  }

  nextStep(wizard: SbWizardComponent, nextTab: SbWizardTabComponent) {
    wizard.selectTab(nextTab);
  }

  private refreshFromServer() {
    this.loading = true;
    if (!this.productCategoryId) {
      this.productCategory = new ProductCategoryFullDTO();
      this.loading = false;
    } else {
      this.api
        .pm_ProductCategory_retrieveById(this.productCategoryId)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (data: ProductCategoryFullDTO) => {
            this.productCategory = data;
            this.updateForm();
            this.ambianceUrl = this.productCategory?.illustration
              ? this.util.appendQueryParamToUrl(this.productCategory.illustration.url as unknown as string, '')
              : './assets/img/channel-placeholder.png';
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  updateProductCategoryCall() {
    this.loading = true;
    if (!this.productCategory.id) {
      if (this.productCategory.name) {
        this.api
          .pm_ProductCategory_createProductCategory(this.productCategory)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            take(1),
            finalize(() => {
              this.loading = false;
            })
          )
          .subscribe({
            next: (createdProductCategory: ProductCategoryFullDTO) => {
              this.productCategory = createdProductCategory;
              this.navService.navigateToPoint(this.util.RoutingID.productCategories);
            },
            error: error => {
              this.errorHandler.errorHandler(error);
            },
          });
      }
    } else {
      this.api
        .pm_ProductCategory_editProductCategory(this.productCategory)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          take(1),
          finalize(() => {
            this.loading = false;
          })
        )
        .subscribe({
          next: (editedProductCategory: ProductCategoryFullDTO) => {
            this.productCategory = editedProductCategory;
            this.navService.navigateToPoint(this.util.RoutingID.productCategories);
          },
          error: error => {
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  pickAmbiance() {
    this.AmbianceImageUpload?.destroySubscriptions();
    this.api
      .pm_ProductCategory_retrieveById(this.productCategoryId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (productCategory: ProductCategoryFullDTO) => {
          this.AmbianceImageUpload.imageTransfer = this.imageUploadService.imageUpload(this.translate.instant('lang.globals.ambiance'));
          this.AmbianceImageUpload.imageTransfer.image = productCategory?.illustration;
          this.AmbianceImageUpload.subClippingChange = this.AmbianceImageUpload.imageTransfer.clippingChange.subscribe((res: Clipping) => {
            this.ambianceClippingChange(res);
          });
          this.AmbianceImageUpload.subFileChange = this.AmbianceImageUpload.imageTransfer.fileChange.subscribe((file: SingleFileUpload) => {
            this.ambianceFileChange(file);
          });
          this.AmbianceImageUpload.subDeletePress = this.AmbianceImageUpload.imageTransfer.deletePress.subscribe(() => {
            this.ambianceDeletePress();
          });
          this.AmbianceImageUpload.subModalClosed = this.AmbianceImageUpload.imageTransfer.modalClosed.subscribe(() => {
            this.ambianceUploadModalClosed();
          });
        },
        error: error => {
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceClippingChange(res: Clipping) {
    if (res) {
      this.applyClipping(this.AmbianceImageUpload, res);
      const transImage: FixedAspectRatioImageDTO = this.AmbianceImageUpload.imageTransfer.image;
      this.AmbianceImageUpload.imageTransfer.image = null;
      this.AmbianceImageUpload.imageTransfer.uploading = true;
      this.api
        .pm_ProductCategory_clipIllustration(this.productCategoryId, transImage)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe({
          next: (image: FixedAspectRatioImageDTO) => {
            this.AmbianceImageUpload.imageTransfer.image = image;
            this.AmbianceImageUpload.imageTransfer.uploading = false;
            this.refreshFromServer();
          },
          error: error => {
            this.modal.close('sbNavigationModal');
            this.errorHandler.errorHandler(error);
          },
        });
    }
  }

  ambianceFileChange(file: SingleFileUpload) {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;
    this.api
      .pm_ProductCategory_uploadIllustration(this.productCategoryId, file)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: FixedAspectRatioImageDTO) => {
          this.AmbianceImageUpload.imageTransfer.image = res;
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceDeletePress() {
    this.AmbianceImageUpload.imageTransfer.image = null;
    this.AmbianceImageUpload.imageTransfer.uploading = true;
    this.api
      .pm_ProductCategory_deleteIllustration(this.productCategoryId)
      .pipe(takeUntilDestroyed(this.destroyRef), take(1))
      .subscribe({
        next: (res: boolean) => {
          if (res) {
            this.AmbianceImageUpload.imageTransfer.image = null;
          }
          this.AmbianceImageUpload.imageTransfer.uploading = false;
          this.refreshFromServer();
        },
        error: error => {
          this.modal.close('sbNavigationModal');
          this.errorHandler.errorHandler(error);
        },
      });
  }

  ambianceUploadModalClosed() {
    this.AmbianceImageUpload.destroySubscriptions();
    this.refreshFromServer();
  }

  applyClipping(imageUpload: ImageUploadModel, res: Clipping) {
    imageUpload.imageTransfer.image.clippingTLX = res.clippingTLX;
    imageUpload.imageTransfer.image.clippingTLY = res.clippingTLY;
    imageUpload.imageTransfer.image.clippingBRX = res.clippingBRX;
    imageUpload.imageTransfer.image.clippingBRY = res.clippingBRY;
  }
}
